import { BatchMultipleUnderlyingsComponent } from 'components/common-form/builder/form/node/field/BatchMultipleUnderlyingsField/MultipleUnderlyingsComponent/BatchMultipleUnderlyingsComponent';
import { UnderlyingResults } from 'components/common-form/builder/form/node/field/BatchMultipleUnderlyingsField/MultipleUnderlyingsComponent/model';
import { ValidTags } from 'components/common-form/builder/form/node/field/BatchMultipleUnderlyingsField/MultipleUnderlyingsComponent/ValidTags';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { useData } from 'components/common-form/hooks/useData';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { useUnderlyingSearchByFormId } from 'hooks/underlyings/use-underlying-search';
import { useUnderlyingCodeType } from 'hooks/useUnderlyingCodeType';
import type { Underlying } from 'models/underlying';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { FC, useContext } from 'react';
import { useIntl } from 'react-intl';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';

type Props = ProductNodeBuilderProps & {
  useOrientation?: boolean;
  ResultsComponent?: FC<UnderlyingResults>;
};

export function BatchMultipleUnderlyingsField(props: Props) {
  const fieldId = props.node.id;
  const underlyingsSearch = useUnderlyingSearchByFormId(props.tabId, fieldId);
  return <BatchMultipleUnderlyingsWrapper {...props} {...underlyingsSearch} />;
}

export function BatchMultipleUnderlyingsWrapper({
  node: { id },
  useOrientation = true,
  ResultsComponent,
  tabIndex,
  search,
  getOptionsFromPastedLabels,
}: Props & UnderlyingSearch) {
  const intl = useIntl();

  const [currentValue, setValue] = useData<ReadonlyArray<Underlying>>(id, []);
  const { isCellInactive, isSelected, lostInputBeforeEditing, onEditingKeyDown, setLostInputBeforeEditing } =
    useContext(BatchNavigationContext);
  const isFieldInvalid = useIsFieldInvalid(id);
  const isReadOnly = useBlockOrFieldReadOnly(id);
  const codeType = useUnderlyingCodeType();
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const resetLostInputBeforeEditing = () => setLostInputBeforeEditing?.(undefined);
  const component = (
    <BatchMultipleUnderlyingsComponent
      id={id}
      getOptionsFromPastedLabels={getOptionsFromPastedLabels}
      search={search}
      key={id}
      ResultsComponent={ResultsComponent ?? ValidTags}
      value={currentValue}
      onChange={underlyings => {
        setValue(underlyings);
        resetLostInputBeforeEditing();
      }}
      onInputChanged={resetLostInputBeforeEditing}
      onTagsChanged={resetLostInputBeforeEditing}
      isDisabled={isReadOnly}
      placeholder={intl.formatMessage({ id: 'underlying.placeholder' })}
      className={isFieldInvalid ? 'is-warning' : ''}
      defaultUnderlyingCodeType={codeType}
      tabIndex={tabIndex}
      isCellInactive={isCellInactive}
      onEditingKeyDown={onEditingKeyDown}
      isFocused={isSelected}
      lostInputBeforeEditing={lostInputBeforeEditing}
    />
  );
  if (useOrientation) {
    return (
      <BlockOrientationFormGroup inputId={id} workspaceType="newBatch" className={patchedFieldClassName}>
        {component}
      </BlockOrientationFormGroup>
    );
  }
  return component;
}

BatchMultipleUnderlyingsField.displayName = 'BatchMultipleUnderlyingsField';
